import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { User, Award, Clock, Heart, Star, CheckCircle, MessageCircle, Phone, Shield, Utensils } from "lucide-react";
import { Button } from "../components/ui/button";
import { childcareData } from "../data/mock";

const About = () => {
  const handleWhatsAppClick = (phone, name) => {
    const message = `Hello ${name}! I'm interested in learning more about Heathbourne House Childcare.`;
    const whatsappUrl = `https://wa.me/${phone.replace('+', '')}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <div className="min-h-screen">
      <Header />
      <main className="pt-20">
        
        {/* Hero Section */}
        <section className="py-20 bg-blue-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h1 className="text-4xl md:text-6xl font-bold text-slate-800 mb-6">
                About 
                <span style={{color: '#F8BBD9'}}> Heathbourne</span>
                <span style={{color: '#A7C7E7'}}> House</span>
              </h1>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                Learn about our experienced team and discover what makes our childcare approach so special.
              </p>
            </div>
          </div>
        </section>

        {/* Our Story */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-6">
                  Our Story & Heritage
                </h2>
                <div className="space-y-6 text-slate-600 leading-relaxed">
                  <p>
                    Nestled in the heart of Bushey Heath, Heathbourne House occupies a stunning Georgian/Victorian residence dating back to 1837. This beautiful historic home provides the perfect backdrop for nurturing young minds in an environment that feels both grand and homely.
                  </p>
                  <p>
                    Since 2016, we have been providing exceptional childcare services, combining traditional values with modern educational approaches. Our unique setting spans 2 acres of carefully maintained grounds, complete with farm animals, tennis court, and extensive gardens that serve as natural classrooms.
                  </p>
                  <p>
                    What sets us apart is our commitment to excellence through our tested settler regime, professional curriculum, and the genuine care that comes from our experienced, family-run approach.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_cozy-care/artifacts/t4wdmztk_PHOTO-2025-08-25-17-46-28.jpg"
                  alt="Our beautiful gardens"
                  className="rounded-2xl shadow-lg"
                />
                <img 
                  src="https://customer-assets.emergentagent.com/job_30df0788-29ee-48d3-84a9-89ecbb62c5b6/artifacts/yyu61zk7_PHOTO-2025-08-25-17-46-27-1.jpg"
                  alt="Indoor learning spaces"
                  className="rounded-2xl shadow-lg mt-8"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Founders Section */}
        <section className="py-20 bg-pink-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-6">
                Meet Our 
                <span className="text-pink-500"> Co-Founders</span>
              </h2>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                Our experienced, NVQ certified founders bring together decades of childcare expertise with genuine care and dedication.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {childcareData.about.foundersInfo.map((member, index) => (
                <div key={index} className="bg-white rounded-3xl p-8 border border-pink-100 hover:shadow-xl transition-all">
                  <div className="flex flex-col items-center text-center mb-6">
                    {member.photo ? (
                      <div className="w-32 h-32 rounded-2xl overflow-hidden mb-4 shadow-lg">
                        <img 
                          src={member.photo} 
                          alt={member.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="w-32 h-32 bg-pink-100 rounded-2xl flex items-center justify-center mb-4 shadow-lg">
                        <User className="h-16 w-16 text-pink-500" />
                      </div>
                    )}
                    <h3 className="text-2xl font-bold text-slate-800 mb-2">{member.name}</h3>
                    <p className="text-pink-500 font-semibold mb-4">{member.role}</p>
                  </div>
                  
                  <div className="space-y-4 mb-6">
                    <div className="flex items-center justify-center space-x-2">
                      <Clock className="h-4 w-4 text-blue-400" />
                      <span className="text-sm text-slate-600">{member.experience}</span>
                    </div>
                    <div className="flex items-center justify-center space-x-2">
                      <Award className="h-4 w-4 text-green-500" />
                      <span className="text-sm text-slate-600">{member.qualifications}</span>
                    </div>
                    <div className="flex items-center justify-center space-x-2">
                      <Star className="h-4 w-4 text-pink-400" />
                      <span className="text-sm text-slate-600">{member.specialization}</span>
                    </div>
                  </div>
                  
                  <p className="text-slate-600 leading-relaxed mb-6 text-center">{member.description}</p>
                  
                  {/* Contact buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button
                      onClick={() => handleWhatsAppClick(member.phone, member.name)}
                      className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-full text-sm transition-colors flex items-center justify-center"
                    >
                      <MessageCircle className="h-4 w-4 mr-2" />
                      WhatsApp {member.name}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => window.location.href = `tel:${member.phone}`}
                      className="border-2 border-pink-400 text-pink-400 hover:bg-pink-400 hover:text-white px-4 py-2 rounded-full text-sm transition-colors flex items-center justify-center"
                    >
                      <Phone className="h-4 w-4 mr-2" />
                      Call {member.name}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Tested Settler Regime */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-6">
                Our 
                <span className="text-blue-500"> Tested Settler Regime</span>
              </h2>
              <p className="text-xl text-slate-600 max-w-4xl mx-auto">
                {childcareData.about.settlerRegime.description}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {childcareData.about.settlerRegime.steps.map((step, index) => (
                <div key={index} className="bg-blue-50 rounded-3xl p-8 text-center border border-blue-100 hover:shadow-lg transition-all">
                  <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-6">
                    <span className="text-white font-bold text-2xl">{step.step}</span>
                  </div>
                  <h3 className="text-xl font-bold text-slate-800 mb-4">{step.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Certifications - More Specific */}
        <section className="py-20 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-12">
              Our Professional 
              <span className="text-blue-500"> Certifications</span>
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              <div className="bg-white rounded-3xl p-8 shadow-lg border border-blue-100 hover:shadow-xl transition-shadow">
                <div className="w-20 h-20 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Award className="h-10 w-10 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-800 mb-4">Ofsted Registered</h3>
                <div className="bg-blue-50 rounded-2xl p-4 mb-4">
                  <p className="text-blue-700 font-bold text-lg">EY2527526</p>
                  <p className="text-blue-600 text-sm">Registration Number</p>
                </div>
                <p className="text-slate-600 text-sm">Childcare on Domestic Premises</p>
              </div>
              
              <div className="bg-white rounded-3xl p-8 shadow-lg border border-green-100 hover:shadow-xl transition-shadow">
                <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Utensils className="h-10 w-10 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-800 mb-4">Food Hygiene</h3>
                <div className="bg-green-50 rounded-2xl p-4 mb-4">
                  <p className="text-green-700 font-bold text-lg">Level 2</p>
                  <p className="text-green-600 text-sm">Food Safety & Hygiene</p>
                </div>
                <p className="text-slate-600 text-sm">Certified by Chartered Institute of Environmental Health</p>
              </div>
              
              <div className="bg-white rounded-3xl p-8 shadow-lg border border-pink-100 hover:shadow-xl transition-shadow">
                <div className="w-20 h-20 bg-pink-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="h-10 w-10 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-800 mb-4">NVQ Certified</h3>
                <div className="bg-pink-50 rounded-2xl p-4 mb-4">
                  <p className="text-pink-700 font-bold text-lg">Qualified</p>
                  <p className="text-pink-600 text-sm">Children's Care, Learning & Development</p>
                </div>
                <p className="text-slate-600 text-sm">Both founders certified</p>
              </div>
              
              <div className="bg-white rounded-3xl p-8 shadow-lg border border-red-100 hover:shadow-xl transition-shadow">
                <div className="w-20 h-20 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Heart className="h-10 w-10 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-800 mb-4">First Aid Certified</h3>
                <div className="bg-red-50 rounded-2xl p-4 mb-4">
                  <p className="text-red-700 font-bold text-lg">Paediatric</p>
                  <p className="text-red-600 text-sm">Emergency First Aid</p>
                </div>
                <p className="text-slate-600 text-sm">12-hour certified course, regularly renewed</p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default About;